// The agent detail's header cards (spec 14 §4.3): where the agent works (its
// module 16 memberships, edited through `PUT /v1/agents/:id/scope-bindings`,
// §2.3), and its caps with the tenant's facility money ceiling (§2.12), both
// `agents.config.manage`'s. A save that meets a newer agent shows theirs
// beside yours.

import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { Card } from "../../../../components/card";
import { Field } from "../../../../components/field";
import { Modal } from "../../../../components/modal";
import { Select } from "../../../../components/select";
import { toast } from "../../../../components/toast";
import { minorUnitDigits, normalizeTypedDecimal } from "../../sales/lib/money";
import { formatMinor } from "../../inventory/lib/cost";
import {
  agentsKeys,
  patchAgent,
  patchAgentSettings,
  problemCode,
  putScopeBindings,
  useAgentDepartments,
  useAgentFacilities,
  useAgentSettings,
} from "../hooks/agents-queries";
import {
  majorToMinor,
  minorToMajorText,
  parseCount,
  type AgentDetail,
  type AgentMembership,
} from "../lib/agents-lib";
import { errorText } from "../lib/labels";

type ScopeType = AgentMembership["scope_type"];

interface Binding {
  readonly scope_type: ScopeType;
  readonly facility_id?: string | undefined;
  readonly department_id?: string | undefined;
}

function bindingsOf(memberships: readonly AgentMembership[]): Binding[] {
  return memberships.map((m) => ({
    scope_type: m.scope_type,
    ...(m.facility_id !== null ? { facility_id: m.facility_id } : {}),
    ...(m.department_id !== null ? { department_id: m.department_id } : {}),
  }));
}

function bindingKey(b: Binding): string {
  return `${b.scope_type}:${b.facility_id ?? ""}:${b.department_id ?? ""}`;
}

function BindingLabel({ binding }: { binding: Binding }) {
  const { t } = useTranslation();
  const facilities = useAgentFacilities();
  const departments = useAgentDepartments(
    binding.scope_type === "department" ? binding.facility_id : undefined,
  );
  const facility = facilities.data?.find((f) => f.id === binding.facility_id)?.name ?? "";
  const department = departments.data?.find((d) => d.id === binding.department_id)?.name ?? "";
  if (binding.scope_type === "group") return <>{t("agents.detail.memberships.group")}</>;
  if (binding.scope_type === "facility") {
    return <>{t("agents.detail.memberships.facility", { facility })}</>;
  }
  return <>{t("agents.detail.memberships.department", { facility, department })}</>;
}

export function MembershipsCard({ agent, canManage }: { agent: AgentDetail; canManage: boolean }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const facilities = useAgentFacilities(canManage);
  const [draft, setDraft] = useState<readonly Binding[] | undefined>(undefined);
  const [scope, setScope] = useState<ScopeType>("facility");
  const [facilityId, setFacilityId] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const departments = useAgentDepartments(
    scope === "department" && facilityId !== "" ? facilityId : undefined,
  );
  const [busy, setBusy] = useState(false);
  const bindings = draft ?? bindingsOf(agent.memberships);
  const dirty = draft !== undefined;

  const add = () => {
    const next: Binding =
      scope === "group"
        ? { scope_type: "group" }
        : scope === "facility"
          ? { scope_type: "facility", facility_id: facilityId }
          : { scope_type: "department", facility_id: facilityId, department_id: departmentId };
    if (bindings.some((b) => bindingKey(b) === bindingKey(next))) return;
    setDraft([...bindings, next]);
    setFacilityId("");
    setDepartmentId("");
  };
  const addable =
    scope === "group" ||
    (scope === "facility" ? facilityId !== "" : facilityId !== "" && departmentId !== "");

  const save = () => {
    setBusy(true);
    void putScopeBindings(
      agent.id,
      bindings.map((b) => ({
        scope_type: b.scope_type,
        ...(b.facility_id !== undefined ? { facility_id: b.facility_id } : {}),
        ...(b.department_id !== undefined ? { department_id: b.department_id } : {}),
      })),
    )
      .then(() => {
        toast.success(t("agents.detail.memberships.saved"));
        setDraft(undefined);
      })
      .catch((error: unknown) => {
        toast.error(errorText(t, error));
      })
      .finally(() => {
        setBusy(false);
        void queryClient.invalidateQueries({ queryKey: agentsKeys.detail(agent.id) });
      });
  };

  return (
    <Card title={t("agents.detail.memberships.title")} data-testid="agent-memberships">
      <Card.Body className="ag-stack">
        {bindings.length === 0 ? (
          <p className="ag-hint">{t("agents.detail.memberships.empty")}</p>
        ) : (
          <ul className="ag-memberships">
            {bindings.map((b) => (
              <li key={bindingKey(b)} className="ag-membership" data-testid="agent-membership">
                <span>
                  <BindingLabel binding={b} />
                </span>
                {canManage ? (
                  <Button
                    onClick={() => {
                      setDraft(bindings.filter((x) => bindingKey(x) !== bindingKey(b)));
                    }}
                  >
                    {t("agents.detail.memberships.remove")}
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        {canManage ? (
          <div className="ag-field-row">
            <Field label={t("agents.detail.memberships.scopeType")}>
              <Select
                value={scope}
                onChange={(e) => {
                  setScope(e.target.value as ScopeType);
                  setDepartmentId("");
                }}
                data-testid="membership-scope"
              >
                {(["group", "facility", "department"] as const).map((s) => (
                  <option key={s} value={s}>
                    {t(`agents.detail.memberships.scope.${s}`)}
                  </option>
                ))}
              </Select>
            </Field>
            {scope !== "group" ? (
              <Field label={t("agents.detail.memberships.facilityPick")}>
                <Select
                  value={facilityId}
                  onChange={(e) => {
                    setFacilityId(e.target.value);
                    setDepartmentId("");
                  }}
                  data-testid="membership-facility"
                >
                  <option value="">{t("agents.detail.memberships.choose")}</option>
                  {(facilities.data ?? []).map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name}
                    </option>
                  ))}
                </Select>
              </Field>
            ) : null}
            {scope === "department" ? (
              <Field label={t("agents.detail.memberships.departmentPick")}>
                <Select
                  value={departmentId}
                  onChange={(e) => {
                    setDepartmentId(e.target.value);
                  }}
                  data-testid="membership-department"
                >
                  <option value="">{t("agents.detail.memberships.choose")}</option>
                  {(departments.data ?? []).map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </Select>
              </Field>
            ) : null}
            <Button disabled={!addable} onClick={add} data-testid="membership-add">
              {t("agents.detail.memberships.add")}
            </Button>
            {dirty ? (
              <>
                <Button
                  onClick={() => {
                    setDraft(undefined);
                  }}
                  disabled={busy}
                >
                  {t("agents.common.discard")}
                </Button>
                <Button
                  variant="primary"
                  onClick={save}
                  disabled={busy}
                  data-testid="membership-save"
                >
                  {busy ? t("agents.common.saving") : t("agents.common.save")}
                </Button>
              </>
            ) : null}
          </div>
        ) : null}
      </Card.Body>
    </Card>
  );
}

interface CapsDraft {
  readonly actions: string;
  readonly spend: string;
  readonly tokens: string;
}

export function CapsCard({ agent }: { agent: AgentDetail }) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const currency = agent.caps.spend_cap_currency;
  const digits = minorUnitDigits(currency, i18n.language);
  const settings = useAgentSettings();
  const initial: CapsDraft = {
    actions: String(agent.caps.monthly_action_cap),
    spend: minorToMajorText(agent.caps.monthly_spend_cap_minor, digits),
    tokens: String(agent.caps.daily_token_budget),
  };
  const [draft, setDraft] = useState<CapsDraft>(initial);
  const [error, setError] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState<AgentDetail | undefined>(undefined);
  const [ceiling, setCeiling] = useState<string | undefined>(undefined);
  const ceilingCurrency = settings.data?.facility_policy_money_ceiling_currency;
  const ceilingDigits =
    ceilingCurrency === undefined ? 2 : minorUnitDigits(ceilingCurrency, i18n.language);

  const parsed = () => {
    const actions = parseCount(draft.actions, 1_000_000);
    const tokens = parseCount(draft.tokens, 1_000_000_000);
    const spend = majorToMinor(normalizeTypedDecimal(draft.spend, i18n.language), digits);
    return { actions, tokens, spend };
  };

  const send = (version: number) => {
    const { actions, tokens, spend } = parsed();
    if (actions === undefined || tokens === undefined) {
      setError(t("agents.detail.caps.invalid"));
      return;
    }
    if (spend === undefined) {
      setError(t("agents.detail.caps.invalidMoney"));
      return;
    }
    setError(undefined);
    setBusy(true);
    void patchAgent(agent.id, {
      monthly_action_cap: actions,
      monthly_spend_cap_minor: spend,
      daily_token_budget: tokens,
      version,
    })
      .then(() => {
        toast.success(t("agents.detail.caps.saved"));
        setConflict(undefined);
      })
      .catch(async (err: unknown) => {
        if (problemCode(err) === "AGENT_VERSION_CONFLICT") {
          await queryClient.invalidateQueries({ queryKey: agentsKeys.detail(agent.id) });
          const fresh = queryClient.getQueryData<AgentDetail>(agentsKeys.detail(agent.id));
          setConflict(fresh ?? agent);
          return;
        }
        toast.error(errorText(t, err));
      })
      .finally(() => {
        setBusy(false);
        void queryClient.invalidateQueries({ queryKey: agentsKeys.all });
      });
  };

  const saveCeiling = () => {
    if (ceiling === undefined || ceilingCurrency === undefined) return;
    const minor = majorToMinor(normalizeTypedDecimal(ceiling, i18n.language), ceilingDigits);
    if (minor === undefined) {
      setError(t("agents.detail.caps.invalidMoney"));
      return;
    }
    setBusy(true);
    void patchAgentSettings({ facility_policy_money_ceiling_minor: minor })
      .then(() => {
        toast.success(t("agents.detail.caps.ceilingSaved"));
        setCeiling(undefined);
      })
      .catch((err: unknown) => {
        toast.error(errorText(t, err));
      })
      .finally(() => {
        setBusy(false);
        void queryClient.invalidateQueries({ queryKey: agentsKeys.settings() });
      });
  };

  const dirty =
    draft.actions !== initial.actions ||
    draft.spend !== initial.spend ||
    draft.tokens !== initial.tokens;

  return (
    <Card title={t("agents.detail.caps.title")} data-testid="agent-caps">
      <Card.Body className="ag-stack">
        <div className="ag-field-row">
          <Field label={t("agents.detail.caps.monthlyActions")}>
            <input
              className="ds-input ag-input-narrow"
              inputMode="numeric"
              value={draft.actions}
              onChange={(e) => {
                setDraft({ ...draft, actions: e.target.value });
              }}
              data-testid="caps-actions"
            />
          </Field>
          <Field label={t("agents.detail.caps.monthlySpend", { currency })}>
            <input
              className="ds-input ag-input-narrow"
              inputMode="decimal"
              value={draft.spend}
              onChange={(e) => {
                setDraft({ ...draft, spend: e.target.value });
              }}
              data-testid="caps-spend"
            />
          </Field>
          <Field label={t("agents.detail.caps.dailyTokens")}>
            <input
              className="ds-input ag-input-narrow"
              inputMode="numeric"
              value={draft.tokens}
              onChange={(e) => {
                setDraft({ ...draft, tokens: e.target.value });
              }}
              data-testid="caps-tokens"
            />
          </Field>
          {dirty ? (
            <Button
              variant="primary"
              disabled={busy}
              onClick={() => {
                send(agent.version);
              }}
              data-testid="caps-save"
            >
              {busy ? t("agents.common.saving") : t("agents.common.save")}
            </Button>
          ) : null}
        </div>
        {settings.data !== undefined && ceilingCurrency !== undefined ? (
          <div className="ag-field-row">
            <Field label={t("agents.detail.caps.ceiling", { currency: ceilingCurrency })}>
              <input
                className="ds-input ag-input-narrow"
                inputMode="decimal"
                value={
                  ceiling ??
                  minorToMajorText(settings.data.facility_policy_money_ceiling_minor, ceilingDigits)
                }
                onChange={(e) => {
                  setCeiling(e.target.value);
                }}
                data-testid="caps-ceiling"
              />
            </Field>
            {ceiling !== undefined ? (
              <Button
                variant="primary"
                disabled={busy}
                onClick={saveCeiling}
                data-testid="caps-ceiling-save"
              >
                {t("agents.common.save")}
              </Button>
            ) : null}
          </div>
        ) : null}
        {error !== undefined ? (
          <p className="ag-error" role="alert">
            {error}
          </p>
        ) : null}
      </Card.Body>
      {conflict !== undefined ? (
        <Modal
          label={t("agents.detail.conflict.title")}
          heading={t("agents.detail.conflict.title")}
          closeLabel={t("agents.common.close")}
          onClose={() => {
            setConflict(undefined);
          }}
          pending={busy}
          testId="caps-conflict"
        >
          <div className="ag-stack">
            <p className="ag-hint">{t("agents.detail.conflict.body")}</p>
            <div className="ag-compare">
              <dl className="ag-detail-list" data-testid="caps-conflict-theirs">
                <dt>{t("agents.detail.conflict.theirs")}</dt>
                <dd />
                <dt>{t("agents.detail.caps.monthlyActions")}</dt>
                <dd>{conflict.caps.monthly_action_cap.toLocaleString(i18n.language)}</dd>
                <dt>{t("agents.detail.caps.monthlySpend", { currency })}</dt>
                <dd>
                  {formatMinor(conflict.caps.monthly_spend_cap_minor, currency, i18n.language)}
                </dd>
                <dt>{t("agents.detail.caps.dailyTokens")}</dt>
                <dd>{conflict.caps.daily_token_budget.toLocaleString(i18n.language)}</dd>
              </dl>
              <dl className="ag-detail-list" data-testid="caps-conflict-yours">
                <dt>{t("agents.detail.conflict.yours")}</dt>
                <dd />
                <dt>{t("agents.detail.caps.monthlyActions")}</dt>
                <dd>{draft.actions}</dd>
                <dt>{t("agents.detail.caps.monthlySpend", { currency })}</dt>
                <dd>{draft.spend}</dd>
                <dt>{t("agents.detail.caps.dailyTokens")}</dt>
                <dd>{draft.tokens}</dd>
              </dl>
            </div>
            <div className="ag-decide">
              <Button
                onClick={() => {
                  setDraft({
                    actions: String(conflict.caps.monthly_action_cap),
                    spend: minorToMajorText(conflict.caps.monthly_spend_cap_minor, digits),
                    tokens: String(conflict.caps.daily_token_budget),
                  });
                  setConflict(undefined);
                }}
                data-testid="caps-conflict-keep"
              >
                {t("agents.detail.conflict.keepTheirs")}
              </Button>
              <Button
                variant="primary"
                disabled={busy}
                onClick={() => {
                  send(conflict.version);
                }}
                data-testid="caps-conflict-apply"
              >
                {t("agents.detail.conflict.applyMine")}
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
    </Card>
  );
}
